<template>
  <div class="city-cont">
    <ProgressBar
      :lengthBar="lengthBar"
      :skewBar="skewBar"
    />
    <div class="scrollbar">
      <Hero :heroEntered="heroEntered"/>
        <main class="page-city__cont">
          <component
            v-for="component in reorderComponent"
            :is="component.name"
            :key="component.order"
          />
        </main>
    </div>
  </div>
</template>

<script>
import gsap from 'gsap';
import { mapGetters } from 'vuex';
import Scrollbar, { ScrollbarPlugin } from 'smooth-scrollbar';

import store from '@/store';

import Hero from '@/components/Hero';
import ProgressBar from '@/components/ProgressBar';
import Intro from '@/components/Intro';
import Quote from '@/components/Quote';
import Description from '@/components/Description';
import Map from '@/components/Map';
import CityFace from '@/components/CityFace';
import Diary from '@/components/Diary';
import PhotoSlider from '@/components/PhotoSlider';
import Closing from '@/components/Closing';
import Video from '@/components/Video';
import CityFooter from '@/components/CityFooter';

import animations from '@/scripts/animations';
import { hasClass, viewportSize } from '@/scripts/utils';

export default {
  name: 'city',

  components: {
    Hero,
    ProgressBar,
    Intro,
    Quote,
    Description,
    Map,
    CityFace,
    Diary,
    PhotoSlider,
    Closing,
    Video,
    CityFooter,
  },

  data() {
    return {
      page: null,
      dataPage: false,
      parallaxEls: null,
      heroIn: false,
      lengthBar: 0,
      skewBar: 0,
      prevDir: 'up',
      prevValDir: 0,
      lastTime: Date.now(),
      lastOffset: 0,
      reduceAmount: 0,
    };
  },

  created() {
    this.page = this.$route.params.city;
  },

  computed: {
    ...mapGetters(['getDataCity']),

    reorderComponent() {
      this.dataPage = this.$store.getters.getDataCity(this.page).data;
      return this.dataPage.composition.sort((a, b) => a.order - b.order);
    },
  },

  mounted() {
    this.menu = document.querySelector('header');
    if (hasClass(this.menu, 'hide')) this.menu.classList.remove('hide');
    if (hasClass(this.menu, 'theme-dark')) this.menu.classList.remove('theme-dark');

    this.$store.commit('ADD_BUTTONS', this.$el);

    class DisableScrollPlugin extends ScrollbarPlugin {
      static pluginName = 'disableScroll';

      static defaultOptions = {
        direction: 'x',
      };

      transformDelta(delta) {
        if (this.options.direction) {
          delta[this.options.direction] = 0;
        }
        return { ...delta };
      }
    }
    Scrollbar.use(DisableScrollPlugin);

    const scrollbar = this.$el.querySelector('.scrollbar');
    this.scrollbar = new Scrollbar(scrollbar);

    this.parallaxEls = Array.from(this.$el.querySelectorAll('[data-parallax]'));
    this.animEls = Array.from(this.$el.querySelectorAll('[data-anim]'));
    gsap.set(this.animEls, { autoAlpha: 0 });

    this.scrollbar.addListener(this.scrollHandler.bind(this));

    this.setEnter();
  },

  methods: {
    setEnter() {
      gsap.set('.introduction', {
        webkitClipPath: 'polygon(0% 0%, 85% 10%, 100% 100%, 0% 100%)',
      });

      gsap.set('.page-city__cont', {
        scaleX: 0,
        backgroundColor: 'transparent',
        transformOrigin: '0 0',
      });
      gsap.set('.intro__title', {
        autoAlpha: 0,
      });
    },

    heroEntered() {
      this.heroIn = true;

      this.enterPage();
    },

    enterPage() {
      const arr1 = [75, 5, 10];
      const arr2 = [100, 0, 0];

      arr2.onUpdate = () => {
        gsap.set('.introduction', {
          webkitClipPath: `polygon(0% 0%, ${arr1[0]}% 0%, 100% 100%, 0% 100%)`,
        });
      };

      gsap.to(arr1, 0.7, arr2).eventCallback('onComplete', () => {
        gsap.set('.introduction', { clearProps: 'all' });
      });
      gsap.to('.page-city__cont', 1, {
        scaleX: 1,
        clearProps: 'all',
        ease: gsap.Expo.easeOut,
        onComplete: () => {
          const title = document.querySelector('.intro__title');
          animations.get('text')(title);
        },
      });
    },

    scrollHandler() {
      this.lengthBar = (this.scrollbar.offset.y / this.scrollbar.limit.y).toFixed(2);
      this.checkMenu();
      this.setSkewBar();

      this.parallaxEls.forEach((el) => {
        if (this.scrollbar.isVisible(el)) {
          animations.get('parallax')(el);
        }
      });

      this.animEls.forEach((animEl) => {
        const type = animEl.getAttribute('data-anim');

        if (this.scrollbar.isVisible(animEl) && !animEl.hasAttribute('data-animated')) {
          const offsetEl = animEl.getBoundingClientRect().top;

          if (offsetEl <= (window.innerHeight / 1.3)) {
            animEl.setAttribute('data-animated', '');

            animations.get(type)(animEl);
          }
        }
      });
    },

    checkMenu() {
      this.direction = this.prevValDir < this.scrollbar.offset.y ? 'down' : 'up';

      this.prevValDir = this.scrollbar.offset.y;

      if (this.scrollbar.offset.y > (viewportSize().y - 160) && !hasClass(this.menu, 'theme-dark')) {
        this.menu.classList.add('theme-dark');
      } else if (this.scrollbar.offset.y < (viewportSize().y - 160) && hasClass(this.menu, 'theme-dark')) {
        this.menu.classList.remove('theme-dark');
      }

      if (this.direction === 'down' && !hasClass(this.menu, 'hide') && this.scrollbar.offset.y > (viewportSize().y / 2)) {
        this.menu.classList.add('hide');
      } else if (this.direction === 'up' && hasClass(this.menu, 'hide')) {
        this.menu.classList.remove('hide');
      }
    },

    setSkewBar() {
      const current = Date.now();
      const offset = this.scrollbar.offset.y;
      let duration = current - this.lastTime;

      if (!duration || offset === this.lastOffset) return;

      if (duration > 100) {
        this.reduceAmount += (duration - 1);
        duration -= (duration - 1);
      }

      const velocity = (offset - this.lastOffset) / duration;
      this.lastTime = current;
      this.lastOffset = offset;

      const skew = Math.min(Math.abs(velocity) * 50, 60);
      this.skewBar = (this.direction === 'up' ? skew : skew * -1);
    },
  },

  beforeDestroy() {
    this.scrollbar.removeListener(this.scrollHandler);

    setTimeout(() => {
      if (hasClass(this.menu, 'hide')) this.menu.classList.remove('hide');
      if (hasClass(this.menu, 'theme-dark')) this.menu.classList.remove('theme-dark');

      this.scrollbar.destroy();
    }, 1400);
  },

  beforeRouteEnter(from, to, next) {
    const city = !to.params.city ? from.params.city : to.params.city;
    document.querySelector('body').classList.add('wait');

    if (!store.getters.getDataCity(city)) {
      console.log('fetching');
      store.dispatch('FETCH_DATACITY', city).then(() => {
        console.log('DATACITY FETCHED 😃');
        next((vm) => {
          vm.dataPage = vm.getDataCity(vm.page).data;
        });
      });
    } else {
      next((vm) => {
        console.log('DATACITY RETRIEVE FROM STORE 🤘');
        vm.dataPage = vm.getDataCity(vm.page).data;
      });
    }
  },

  beforeRouteUpdate(to, from, next) {
    const city = !to.params.city ? from.params.city : to.params.city;

    if (!store.getters.getDataCity(city)) {
      console.log('fetching');
      store.dispatch('FETCH_DATACITY', city).then(() => {
        console.log('DATACITY FETCHED 😃');
        next((vm) => {
          vm.dataPage = vm.getDataCity(vm.page).data;
        });
      });
    } else {
      next((vm) => {
        console.log('DATACITY RETRIEVE FROM STORE 🤘');
        vm.dataPage = vm.getDataCity(vm.page).data;
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/colors';

.city-cont {
  position: relative;
  display: inline-block;

  .scrollbar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  main {
    position: relative;
    width: 100%;
    max-width: 1024px;
    box-sizing: border-box;
    margin: calc(-10vh - 160px) auto;

    background-color: #101010;

    z-index: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/City.vue